import { deleteRequest, getRequest, postRequest } from "lib/core/apiClient";
import { publishPostMessage } from "./helpers";
import { eventTypes } from "./constants";

/**
 * Which surface asked for the wishlist change.
 *
 * These events go to the parent window, and the cart iframe shares an origin
 * with the ghost iframe the merchant SDK talks to. Without this the storefront
 * cannot tell whose result it is reading - a cart heart-click looks exactly
 * like the answer to a pending Shopflo.addToWishlist() call.
 */
export type WishlistEventContext = "CART" | "SDK" | "STORE_PAGE";

export const handleWishlistAddItem = async (
  merchantId: string,
  customerId: string,
  payload: { productId?: string; variantId?: string },
  context: WishlistEventContext,
): Promise<boolean> => {
  if (!Boolean(customerId)) {
    publishPostMessage(eventTypes.WISHLIST_ITEM_ADDED, {
      success: false,
      error: { message: "User is not logged in." },
      context,
    });
    return false;
  }
  try {
    const response = await postRequest(
      `/wishlists/merchant/${merchantId}/customer/${customerId}`,
      { product_id: payload?.productId, variant_id: payload?.variantId },
      "KRATOS_PRIVATE",
    );
    publishPostMessage(eventTypes.WISHLIST_ITEM_ADDED, { success: true, data: response, context });
    return true;
  } catch (error) {
    publishPostMessage(eventTypes.WISHLIST_ITEM_ADDED, { success: false, error, context });
    return false;
  }
};

export const handleWishlistRemoveItem = async (
  merchantId: string,
  customerId: string,
  payload: { productId?: string; variantId?: string },
  context: WishlistEventContext,
): Promise<boolean> => {
  if (!Boolean(customerId)) {
    publishPostMessage(eventTypes.WISHLIST_ITEM_REMOVED, {
      success: false,
      error: { message: "User is not logged in." },
      context,
    });
    return false;
  }
  try {
    const response = await deleteRequest(
      `/wishlists/merchant/${merchantId}/customer/${customerId}`,
      { product_id: payload?.productId, variant_id: payload?.variantId },
      "KRATOS_PRIVATE",
    );
    publishPostMessage(eventTypes.WISHLIST_ITEM_REMOVED, { success: true, data: response, context });
    return true;
  } catch (error) {
    publishPostMessage(eventTypes.WISHLIST_ITEM_REMOVED, { success: false, error, context });
    return false;
  }
};

export const handleWishlistGetItems = async (
  merchantId: string,
  customerId: string,
  payload: { limit?: number; offset?: number },
) => {
  if (!Boolean(customerId)) {
    publishPostMessage(eventTypes.WISHLIST_ITEMS_DATA, {
      success: false,
      error: { message: "User is not logged in." },
    });
    return;
  }
  try {
    const response = await getRequest(
      `/wishlists/merchant/${merchantId}/customer/${customerId}?limit=${payload?.limit ?? 10}&offset=${
        payload?.offset ?? 0
      }`,
      "KRATOS_PRIVATE",
    );
    publishPostMessage(eventTypes.WISHLIST_ITEMS_DATA, { success: true, data: response });
  } catch (error) {
    publishPostMessage(eventTypes.WISHLIST_ITEMS_DATA, { success: false, error });
  }
};
